/**
 * Utility functions for grouping form fields under subheadings
 */

/**
 * Groups form data by subheadings based on form schema
 * @param {Object} formData - The form data to group
 * @param {Array} formSchema - The form schema with field definitions
 * @returns {Object} - Grouped form data
 */
export const groupFormDataBySubheadings = (formData, formSchema) => {
  const groupedData = {};
  const ungroupedData = {};
  const subheadingInfo = {}; // Store subheading metadata

  // Check if formSchema is null or undefined
  if (!formSchema || !Array.isArray(formSchema)) {
    return groupedData;
  }

  // Process each field in the schema
  formSchema.forEach((fieldEntry) => {
    if (!fieldEntry || !Array.isArray(fieldEntry) || fieldEntry.length < 2) {
      return; // Skip invalid entries
    }
    
    const [fieldKey, fieldConfig] = fieldEntry;
    
    if (fieldConfig.type === "subheading") {
      // Store subheading metadata
      subheadingInfo[fieldKey] = {
        title: fieldKey,
        type: "subheading",
        headingLevel: fieldConfig.headingLevel || "h3",
        subheadingDescription: fieldConfig.subheadingDescription || "",
        displayOrder: fieldConfig.displayOrder || 0
      };
      
      // Initialize subheading group if it doesn't exist
      if (!groupedData[fieldKey]) {
        groupedData[fieldKey] = {
          _subheading: subheadingInfo[fieldKey], // Include metadata
          _fields: {} // Separate container for field data
        };
      }
    } else if (fieldKey in formData) {
      // This is a regular field with data - check if it has an explicit group assignment
      const fieldValue = formData[fieldKey];
      const assignedGroup = fieldConfig.assignedGroup;
      
      if (assignedGroup && assignedGroup.trim() !== "" && subheadingInfo[assignedGroup]) {
        // Field has explicit group assignment to a valid subheading
        if (!groupedData[assignedGroup]) {
          groupedData[assignedGroup] = {
            _subheading: subheadingInfo[assignedGroup],
            _fields: {}
          };
        }
        groupedData[assignedGroup]._fields[fieldKey] = fieldValue;
      } else {
        // Field has no group assignment or invalid group - put in ungrouped
        ungroupedData[fieldKey] = fieldValue;
      }
    }
  });

  // Create a cleaner nested structure
  const result = {};
  
  // Add ungrouped fields at root level if any exist
  if (Object.keys(ungroupedData).length > 0) {
    result._ungroupedFields = ungroupedData;
  }
  
  // Add grouped fields with proper nesting
  Object.keys(groupedData).forEach(groupKey => {
    const group = groupedData[groupKey];
    if (group._fields && Object.keys(group._fields).length > 0) {
      // Create properly nested structure with subheading metadata and fields
      result[groupKey] = {
        _metadata: group._subheading,
        _fields: group._fields
      };
    }
  });

  return result;
};

/**
 * Flattens grouped form data back to a flat structure
 * @param {Object} groupedData - The grouped form data
 * @returns {Object} - Flattened form data
 */
export const flattenGroupedFormData = (groupedData) => {
  const flatData = {};

  Object.keys(groupedData).forEach(key => {
    const value = groupedData[key];
    
    if (key === '_ungroupedFields') {
      // Add ungrouped fields directly to flat data
      Object.assign(flatData, value);
    } else if (typeof value === 'object' && value !== null && value._fields) {
      // This is a grouped structure with _fields, extract the field data
      Object.assign(flatData, value._fields);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Legacy structure - flatten everything
      Object.keys(value).forEach(subKey => {
        if (!subKey.startsWith('_')) { // Skip metadata keys
          flatData[subKey] = value[subKey];
        }
      });
    } else {
      // This is a direct field
      flatData[key] = value;
    }
  });

  return flatData;
};

/**
 * Gets the subheading structure from form schema
 * @param {Array} formSchema - The form schema
 * @returns {Array} - Array of subheading information
 */
export const getSubheadingStructure = (formSchema) => {
  const structure = [];
  const groupMap = {}; // Map group names to their configurations
  const ungroupedFields = [];

  // Check if formSchema is null or undefined
  if (!formSchema || !Array.isArray(formSchema)) {
    return structure;
  }

  // First pass: identify all subheadings and create group map
  formSchema.forEach((fieldEntry) => {
    if (!fieldEntry || !Array.isArray(fieldEntry) || fieldEntry.length < 2) {
      return; // Skip invalid entries
    }
    
    const [fieldKey, fieldConfig] = fieldEntry;
    if (fieldConfig && fieldConfig.type === "subheading") {
      groupMap[fieldKey] = {
        key: fieldKey,
        title: fieldKey,
        level: fieldConfig.headingLevel || 'h3',
        description: fieldConfig.subheadingDescription || '',
        displayOrder: fieldConfig.displayOrder || 0,
        fields: []
      };
    }
  });

  // Second pass: assign fields to their groups based on assignedGroup
  formSchema.forEach((fieldEntry) => {
    if (!fieldEntry || !Array.isArray(fieldEntry) || fieldEntry.length < 2) {
      return; // Skip invalid entries
    }
    
    const [fieldKey, fieldConfig] = fieldEntry;
    
    if (fieldConfig && fieldConfig.type !== "subheading") {
      const fieldInfo = {
        key: fieldKey,
        config: fieldConfig
      };
      
      const assignedGroup = fieldConfig.assignedGroup;
      if (assignedGroup && assignedGroup.trim() !== "" && groupMap[assignedGroup]) {
        // Field is assigned to a specific group
        groupMap[assignedGroup].fields.push(fieldInfo);
      } else {
        // Field has no group assignment
        ungroupedFields.push(fieldInfo);
      }
    }
  });

  // Build final structure from groups that have fields, sorted by displayOrder
  const groupsWithFields = Object.values(groupMap)
    .filter(group => group.fields.length > 0)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  groupsWithFields.forEach(group => {
    // Sort fields within each group by their displayOrder
    group.fields.sort((a, b) => (a.config.displayOrder || 0) - (b.config.displayOrder || 0));
    
    structure.push({
      subheading: {
        key: group.key,
        title: group.title,
        level: group.level,
        description: group.description
      },
      fields: group.fields
    });
  });

  // Add ungrouped fields as a separate section if any exist
  if (ungroupedFields.length > 0) {
    // Sort ungrouped fields by displayOrder too
    ungroupedFields.sort((a, b) => (a.config.displayOrder || 0) - (b.config.displayOrder || 0));
    
    structure.unshift({
      subheading: {
        key: 'Ungrouped',
        title: 'Ungrouped Fields',
        level: 'h3',
        description: 'Fields not assigned to any specific group'
      },
      fields: ungroupedFields
    });
  }

  return structure;
};

/**
 * Validates grouped form data
 * @param {Object} groupedData - The grouped form data
 * @param {Array} formSchema - The form schema
 * @returns {Object} - Validation result with errors grouped by subheading
 */
export const validateGroupedFormData = (groupedData, formSchema) => {
  const errors = {};
  const flatData = flattenGroupedFormData(groupedData);
  
  // Check if formSchema is null or undefined
  if (!formSchema || !Array.isArray(formSchema)) {
    return errors;
  }
  
  // Get subheading structure to organize errors
  const structure = getSubheadingStructure(formSchema);
  
  formSchema.forEach((fieldEntry) => {
    if (!fieldEntry || !Array.isArray(fieldEntry) || fieldEntry.length < 2) {
      return; // Skip invalid entries
    }
    
    const [fieldKey, fieldConfig] = fieldEntry;
    
    if (fieldConfig.type !== "subheading" && fieldConfig.required) {
      const value = flatData[fieldKey];
      
      if (!value || (Array.isArray(value) && value.length === 0) || value === "") {
        // Find which subheading this field belongs to
        let subheadingKey = null;
        
        structure.forEach(group => {
          const fieldInGroup = group.fields.find(f => f.key === fieldKey);
          if (fieldInGroup) {
            subheadingKey = group.subheading.key;
          }
        });
        
        if (subheadingKey) {
          if (!errors[subheadingKey]) {
            errors[subheadingKey] = {};
          }
          errors[subheadingKey][fieldKey] = `${fieldKey} is required`;
        } else {
          errors[fieldKey] = `${fieldKey} is required`;
        }
      }
    }
  });
  
  return errors;
};